import React, { useRef, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Skeleton,
  Alert,
  TextField,
  InputAdornment,
  Stack,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { httpClient } from '@/utils/httpClient';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';
import { usePersonLabels } from '@/hooks/usePersonLabels';

interface AuditRecord {
  id: string;
  audit_timestamp: string;
  operation: 'INSERT' | 'UPDATE' | 'DELETE' | 'ACTIVITY';
  entity_id: string | null;
  entity_type: string | null;
  entity_name: string | null;
  entity_identifier: string | null;
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  change_summary: string;
  changed_fields: string[] | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
}

interface FieldChange {
  field: string;
  label: string;
  oldValue: unknown;
  newValue: unknown;
}

interface EntityActivityPanelProps {
  entityType: string;
  pollingInterval?: number;
  pageSize?: number;
}

// Convert snake_case or camelCase to Title Case
const formatFieldName = (field: string): string => {
  return field
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return value.toLocaleString();
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// System fields that shouldn't be shown as user-meaningful changes
const IGNORED_FIELDS = new Set([
  'updated_at',
  'created_at',
  'version',
  'record_version',
  'effective_from',
  'effective_to',
  'is_current',
  'change_reason',
  'superseded_by',
  'entity_version_group',
  'created_by',
  'updated_by',
  'metadata',
]);

const computeFieldChanges = (
  oldData: Record<string, unknown> | null,
  newData: Record<string, unknown> | null,
): FieldChange[] => {
  const changes: FieldChange[] = [];

  const oldEntityData = (oldData?.entity_data as Record<string, unknown>) || {};
  const newEntityData = (newData?.entity_data as Record<string, unknown>) || {};

  // Get all keys from both old and new entity_data
  const allKeys = new Set([...Object.keys(oldEntityData), ...Object.keys(newEntityData)]);

  for (const key of allKeys) {
    // Skip internal/system fields
    if (IGNORED_FIELDS.has(key) || key.startsWith('_')) {
      continue;
    }

    const oldValue = oldEntityData[key];
    const newValue = newEntityData[key];

    // Normalize undefined to null for comparison
    const oldNormalized = oldValue === undefined ? null : oldValue;
    const newNormalized = newValue === undefined ? null : newValue;

    if (JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized)) {
      changes.push({
        field: key,
        label: formatFieldName(key),
        oldValue,
        newValue,
      });
    }
  }

  // Also check top-level fields that users might change
  const topLevelFields = ['display_name', 'entity_identifier', 'status'];
  for (const field of topLevelFields) {
    const oldValue = oldData?.[field];
    const newValue = newData?.[field];

    const oldNormalized = oldValue === undefined ? null : oldValue;
    const newNormalized = newValue === undefined ? null : newValue;

    if (JSON.stringify(oldNormalized) !== JSON.stringify(newNormalized)) {
      changes.push({
        field,
        label: formatFieldName(field),
        oldValue,
        newValue,
      });
    }
  }

  return changes;
};

const getOperationColor = (operation: AuditRecord['operation']): 'success' | 'error' | 'info' => {
  switch (operation) {
    case 'INSERT':
      return 'success';
    case 'DELETE':
      return 'error';
    default:
      return 'info';
  }
};

const formatTimestamp = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatChangeSummary = (record: AuditRecord): React.ReactNode => {
  if (record.operation === 'INSERT') {
    const createdFromReceipt = (record.new_data as Record<string, Record<string, unknown>> | null)
      ?.entity_data?.created_from_receipt;
    return createdFromReceipt ? 'Received' : 'Created';
  }

  if (record.operation === 'DELETE') {
    return 'Deleted';
  }

  // For updates, compute what actually changed
  const changes = computeFieldChanges(record.old_data, record.new_data);

  if (changes.length === 0) {
    return 'Updated';
  }

  // Show all changes stacked
  return (
    <Stack spacing={0.5}>
      {changes.map((change, index) => (
        <Box key={index}>
          <Typography variant="body2" component="span">
            {change.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {formatValue(change.oldValue)} → {formatValue(change.newValue)}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export const EntityActivityPanel: React.FC<EntityActivityPanelProps> = ({
  entityType,
  pollingInterval = 5000,
  pageSize = 25,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pageSize);
  const { individualIdPrefix } = usePersonLabels();
  const [filterText, setFilterText] = useState('');
  const [debouncedFilter, setDebouncedFilter] = useState('');

  // Debounce filter text to avoid excessive API calls
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterText(event.target.value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedFilter(event.target.value);
      setPage(0);
    }, 300);
  };

  // Fetch activity data with server-side pagination
  const { data, isLoading, error } = useQuery({
    queryKey: ['entity-activity-panel', entityType, page, rowsPerPage, debouncedFilter],
    queryFn: async (): Promise<{ items: AuditRecord[]; totalCount: number }> => {
      const params = new URLSearchParams();
      params.append('limit', String(rowsPerPage));
      params.append('offset', String(page * rowsPerPage));
      params.append('order', 'audit_timestamp.desc');
      params.append('entity_type', `eq.${entityType}`);

      if (debouncedFilter.trim()) {
        const search = `*${debouncedFilter.trim()}*`;
        params.append('or', `(entity_name.ilike.${search},entity_identifier.ilike.${search})`);
      }

      const endpoint = `/api/rest/data/v1/entity_activity?${params.toString()}`;
      const { data: items, count } = await httpClient.getWithCount<AuditRecord>(endpoint);
      return { items, totalCount: count };
    },
    refetchInterval: pollingInterval,
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const rows = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  // useTableSort must be called unconditionally before any early returns to satisfy the Rules of Hooks
  const {
    sortField,
    sortDirection,
    onSort,
    sortedRows: sortedData,
  } = useTableSort(rows as unknown as Record<string, unknown>[]);

  if (isLoading) {
    return (
      <Box>
        {[...Array(10)].map((_, i) => (
          <Skeleton key={i} height={53} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to load activity history
      </Alert>
    );
  }

  if (!rows.length && !debouncedFilter && !isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No activity recorded yet
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Filter by item name or identifier..."
          value={filterText}
          onChange={handleFilterChange}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ width: 300 }}
        />
      </Box>

      {rows.length === 0 && debouncedFilter ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No matching activity found
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <SortableTableCell
                  field="entity_name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                >
                  Item
                </SortableTableCell>
                <SortableTableCell
                  field="change_summary"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                >
                  Change
                </SortableTableCell>
                <SortableTableCell
                  field="user_name"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                >
                  User
                </SortableTableCell>
                <SortableTableCell
                  field="audit_timestamp"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={onSort}
                >
                  Time
                </SortableTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedData as unknown as AuditRecord[]).map((record) => (
                <TableRow key={record.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {record.entity_name ||
                        record.entity_identifier?.replace(/^INDV-/, individualIdPrefix) ||
                        'Unknown'}
                    </Typography>
                    {record.entity_identifier && record.entity_name && (
                      <Typography variant="caption" color="text.secondary">
                        {record.entity_identifier.replace(/^INDV-/, individualIdPrefix)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {record.operation === 'UPDATE' ? (
                      formatChangeSummary(record)
                    ) : (
                      <Chip
                        label={
                          record.change_summary ||
                          (record.operation === 'INSERT'
                            ? (record.new_data as Record<string, Record<string, unknown>> | null)
                                ?.entity_data?.created_from_receipt
                              ? 'Received'
                              : 'Created'
                            : 'Deleted')
                        }
                        size="small"
                        color={getOperationColor(record.operation)}
                        variant="outlined"
                        sx={{ height: 24, fontSize: '0.75rem' }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {record.user_name || record.user_email || 'System'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatTimestamp(record.audit_timestamp)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
      />
    </Box>
  );
};
